
import React, { useEffect, useState, useCallback } from "react";
import { Box, Tabs, Tab, Card, Button, Typography, Stack } from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import ShareIcon from "@mui/icons-material/Share";
import BarChartIcon from "@mui/icons-material/BarChart";
import HistoryIcon from "@mui/icons-material/History";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PeopleIcon from "@mui/icons-material/People";
import DescriptionIcon from "@mui/icons-material/Description";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

import api from "../../utils/api";
import SettingsTab from "./SettingsTab";
import FormBuilderTab from "./FormBuilderTab";
import ShareTab from "./ShareTab";
import ResultsTab from "./ResultsTab";
import HistoryTab from "./HistoryTab";
import IntegrityTab from "./IntegrityTab";
import ConsentTab from "./ConsentTab";
import DocumentsTab from "./DocumentsTab";
import EmailTab from "./EmailTab";

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const ElectionPage = ({ election, onBack }) => {
  const [tab, setTab] = useState(0);
  const [details, setDetails] = useState(election);

  useEffect(() => {
    setDetails(election);
  }, [election]);

  const loadElection = useCallback(async () => {
    if (!election?.id || election.id === "new") return;
    try {
      const res = await api.get(`/elections/${election.id}`);
      setDetails(res.data);
    } catch (error) {
      console.error("Failed to load election details:", error);
    }
  }, [election?.id]);

  useEffect(() => {
    loadElection();
  }, [loadElection]);

  const handleElectionUpdated = (updatedElection) => {
    if (updatedElection && typeof updatedElection === "object") {
      setDetails((prev) => ({ ...(prev || {}), ...updatedElection }));
      return;
    }
    loadElection();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Button
              onClick={onBack}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              sx={{
                mb: 2,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                color: "#2f63ad",
                borderColor: "rgba(47,99,173,0.18)",
                "&:hover": {
                  borderColor: "#2f63ad",
                  backgroundColor: "rgba(47,99,173,0.04)",
                },
              }}
            >
              Back to Elections
            </Button>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#223142",
                mb: 0.5,
              }}
            >
              {details?.title || "New Election"}
            </Typography>

          </Box>
        </Stack>
      </Box>

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 2.5,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Election Workspace
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
              mb: 2,
            }}
          >
            Switch between configuration, ballot builder, sharing, reporting, and audit history.
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Election Tabs"
            sx={{
              minHeight: "auto",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                minHeight: 42,
                color: "#5f6b7a",
                mr: 1,
                px: 2,
                minWidth: "auto",
              },
              "& .Mui-selected": {
                color: "#2f63ad !important",
                backgroundColor: "rgba(47,99,173,0.08)",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .MuiTab-iconWrapper": {
                marginRight: "8px",
              },
            }}
          >
            <Tab icon={<SettingsIcon />} iconPosition="start" label="Settings" />
            <Tab icon={<FormatAlignLeftIcon />} iconPosition="start" label="Form Builder" />
            <Tab icon={<ShareIcon />} iconPosition="start" label="Share" />
            <Tab icon={<BarChartIcon />} iconPosition="start" label="Results" />
            <Tab icon={<HistoryIcon />} iconPosition="start" label="History" />
            <Tab icon={<VerifiedUserIcon />} iconPosition="start" label="Integrity" />
            <Tab icon={<PeopleIcon />} iconPosition="start" label="Consent & Settings" />
            <Tab icon={<DescriptionIcon />} iconPosition="start" label="Documents" />
            <Tab icon={<MailOutlineIcon />} iconPosition="start" label="Invitation Email" />
          </Tabs>
        </Box>
      </Card>

      <Box sx={{ width: "100%" }}>
        {tab === 0 && (
          <SettingsTab
            election={details}
            onDelete={() => onBack()}
            onElectionUpdated={handleElectionUpdated}
          />
        )}

        {tab === 1 && <FormBuilderTab election={details} />}
        {tab === 2 && <ShareTab election={details} />}
        {tab === 3 && <ResultsTab election={details} />}
        {tab === 4 && <HistoryTab election={details} />}
        {tab === 5 && <IntegrityTab election={details} />}
        {tab === 6 && <ConsentTab election={details} />}
        {tab === 7 && <DocumentsTab election={details} />}
        {tab === 8 && <EmailTab election={details} />}
      </Box>
    </Box>
  );
};

export default ElectionPage;
